import * as React from "react";
import { DatePicker } from "../../../../../../../../react-core/Datepicker/DatePicker";
import { MoneyDisplayHelper } from "../../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { Button } from "../../../../../../../../react-core/Html/Button";
import { Checkbox } from "../../../../../../../../react-core/Html/Checkbox";
import { Lightbox, LightboxType } from "../../../../../../../../react-core/Html/Components/Lightbox";
import { FormComponent } from "../../../../../../../../react-core/Html/FormComponent";
import { TextBoxEditor } from "../../../../../../../../react-core/Html/TextBoxEditor";
import { Guid } from "../../../../../../../../react-core/Utils/Guid";
import { JsonDateParser } from "../../../../../../../../react-core/Utils/JsonDateParser";
import { CurrencyCode } from "../../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { ServiceValidationError } from "../../../../../../../../Wrap/Core/DataTypes/ServiceValidation/ServiceValidationError";
import { CgtTransaction } from "../../../../../../../../Wrap/Models/Domains/Transactions/Investments/CgtTransaction";
import { CgtTransactionHistory } from "../../../../../../../../Wrap/Models/Domains/Transactions/Investments/CgtTransactionHistory";
import { DisinvestmentCharge } from "../../../../../../../../Wrap/Models/Domains/Transactions/Investments/DisinvestmentCharge";
import { InvestmentTransactions } from "../../../../../../../../Wrap/Models/Domains/Transactions/Investments/InvestmentTransactions";
import { TableTempElement } from "../../../../../../../Shared/Components/TableTempElement";
import { TransactionAction } from "../Actions/TransactionActions";
import { Contents, InvestmentTransactionTypeMap } from "../Resource/Contents";

interface ITransactionDetailPopupViewProps {
    showBox: boolean;
    onChange: (val: boolean) => void;
    transId: number;
    disinvestmentCharges: Array<DisinvestmentCharge>;
    productType: string;
}

interface ITransactionPopUpState {
    data: InvestmentTransactions;
    isEditingCGT: boolean;
    isLoading: boolean;
    isCgtLoading: boolean;
    isCgtHistoryLoading: boolean;
    cgtTransaction: CgtTransaction;
    cgtTransactionHistory: Array<CgtTransactionHistory>;
    errorMsg: string[];
    cgtTotalValue: number;
    cgtExempt: boolean;
    cgtDateTime: Date;
    cgtUnitCost: number;
    isSaveable: boolean;
}

export class TransactionDetailPopupView extends FormComponent<
    ITransactionDetailPopupViewProps,
    ITransactionPopUpState
> {
    constructor(props: ITransactionDetailPopupViewProps) {
        super(props);
        this.state = {
            data: null,
            isEditingCGT: false,
            isLoading: true,
            isCgtLoading: true,
            isCgtHistoryLoading: true,
            cgtTransaction: null,
            cgtTransactionHistory: [],
            errorMsg: null,
            cgtTotalValue: 0,
            cgtExempt: false,
            cgtDateTime: new Date(),
            cgtUnitCost: null,
            isSaveable: true,
        };
    }

    componentDidMount() {
        this.getData();
    }

    private getData() {
        TransactionAction.getTransactionsByTransId(this.props.transId, (callBack: InvestmentTransactions) => {
            this.setState({
                isLoading: false,
                data: callBack,
            });
        });

        TransactionAction.getCgtTransactionByTransId(this.props.transId, (data: CgtTransaction) => {
            this.setState({
                isCgtLoading: false,
                cgtTransaction: data,
            });
        });

        TransactionAction.getCgtTransactionsHistoryByTransId(
            this.props.transId,
            (data: Array<CgtTransactionHistory>) => {
                this.setState({
                    isCgtHistoryLoading: false,
                    cgtTransactionHistory: data,
                });
            }
        );
    }

    private renderModal(): JSX.Element {
        return (
            <Lightbox
                show={this.props.showBox}
                title={Contents.TransactionDetailsTitle}
                contentAfterTitle={null}
                onClose={() => this.props.onChange(false)}
                type={LightboxType.Modal}
                unclosable={false}
                noOverlay={false}
                noAutofocus={false}
                cssWrapper={"is-default-lightbox"}
            >
                {this.getModalBody()}
            </Lightbox>
        );
    }

    private getModalBody(): JSX.Element {
        const { data, isLoading, isCgtLoading, isCgtHistoryLoading, errorMsg } = this.state;

        if (isLoading || isCgtLoading || isCgtHistoryLoading) {
            return <TableTempElement />;
        }
        return (
            <>
                <div className="content">
                    <table className="simple-table mb-40">
                        <tbody>
                            {this.renderTemplate("ID", data.TransID, true)}
                            {this.renderTemplate("As at", JsonDateParser.getFormatDate(data.Date, "dd/MM/yyyy"))}
                            <tr>
                                <th className="cell-26">CNote ID</th>
                                <td>{data.DocumentId > 0 ? data.DocumentId : null}</td>
                            </tr>
                            {this.renderTemplate("Order ID", data.OrderId == 0 ? "" : data.OrderId,true)}
                            <tr>
                                <th className="cell-26">Pool order ID</th>
                                <td>{data.PoolOrderID > 0 ? data.PoolOrderID : null}</td>
                            </tr>
                            {this.renderTemplate("Reversed", "No")}
                            {this.renderTemplate("Account", data.SubClAccountid)}
                            {this.renderTemplate("Name", data.SearchKey)}
                            {this.renderTemplate("Search key", data.SearchKey)}
                            {this.renderTemplate("Instrument", data.InstrumentCode)}
                            {this.renderTemplate("ISIN", data.IsInCode, true)}
                            {this.renderTemplate("CUSIP", data.CuSip, true)}
                            {this.renderTemplate("SEDOL", data.SeDol, true)}
                            {this.renderTemplate("Type", InvestmentTransactionTypeMap[data.TransSource])}
                            {this.renderTemplate("Description", data.Investments)}
                            {this.renderTemplate(
                                "Quantity",
                                MoneyDisplayHelper.FromatMoneyDisplay(data.Quantity, null, false, 4)
                            )}
                            {this.renderTemplate(
                                "Filled Price",
                                data.FilledPrice == 0 ? "" : data.FilledPrice.toFixed(4)
                            )}
                            {this.renderTemplate("Group 1", data.Group1.toFixed(4))}
                            {this.renderTemplate("Group 2", data.Group2.toFixed(4))}
                            {this.renderTemplate(
                                "Settlement value",
                                data.SettValueExComm == 0 ? "" : data.SettValueExComm.toFixed(2)
                            )}
                            {this.renderTemplate(
                                "Order date time",
                                JsonDateParser.isValidDate(data.DateCreated)
                                    ? JsonDateParser.getFormatDate(data.DateCreated, "dd/MM/yyyy hh:mm:ss")
                                    : ""
                            )}
                            {this.renderTemplate("Unit cost", data.Cost.toFixed(4))}
                            {this.renderTemplate("Total cost", (data.Cost * data.Quantity).toFixed(2))}
                            {this.renderTemplate("Order currency", data.BaseCCY ? data.BaseCCY : "GBP")}

                            {this.renderTemplate("Location", data.Location)}
                            {this.renderTemplate("Note", data.Note)}
                            {this.renderTemplate("Narrative detail", data.NarrativeDetail)}
                            {this.renderTemplate("User", data.UserName)}
                            <tr>
                                <th className="cell-26">User note</th>
                                <td>{data.UserNote}</td>
                            </tr>
                            {this.renderTemplate("Deal comments", data.DealComments, true)}
                            {this.renderTemplate("Status", data.TransStatus)}
                            {this.renderTemplate("UT discount", data.UtDiscount.toFixed(4))}
                            {this.renderTemplate("UT initial charge", data.UtInitialCharge.toFixed(4))}
                            {this.props.disinvestmentCharges.map((disinvestmentCharge, i) => {
                                return (
                                    <>
                                        {this.renderTemplate("Charge type", disinvestmentCharge.ChargeDescription)}
                                        {this.renderTemplate(
                                            "Charge date",
                                            JsonDateParser.getFormatDate(disinvestmentCharge.ChargeDate, "dd/MM/yyyy")
                                        )}
                                        {this.renderTemplate("Charge amount", disinvestmentCharge.ChargeAmount)}
                                    </>
                                );
                            })}
                        </tbody>
                    </table>
                    {this.renderCGTTable()}
                </div>
                <div>{this.renderTransactionHistoryTable()}</div>
                {errorMsg && errorMsg.length > 0 && (
                    <Lightbox
                        show={this.state.errorMsg.length > 0}
                        title={"Alert"}
                        contentAfterTitle={null}
                        onClose={() => this.setState({ errorMsg: [] })}
                        type={LightboxType.Modal}
                        unclosable={true}
                        noOverlay={false}
                        noAutofocus={true}
                        cssWrapper="is-default-lightbox is-narrow"
                    >
                        {this.state.errorMsg.map((error, index) => {
                            return <p key={`error-${index}`}>{error}</p>;
                        })}
                        <button className="btn primary" onClick={() => this.setState({ errorMsg: [] })}>
                            OK
                        </button>
                    </Lightbox>
                )}
            </>
        );
    }

    private renderCGTTable() {
        const { data, isEditingCGT, cgtTransaction, cgtUnitCost, cgtExempt, isSaveable } = this.state;
        const unitCost =
            data.Quantity != 0 && cgtTransaction.CgtTotalValue != 0
                ? (cgtTransaction.CgtTotalValue / Math.abs(data.Quantity)).toFixed(4)
                : null;
        if (this.props.productType != "Personal Portfolio") {
            return null;
        }
        if (data.Location && data.Location.toLocaleLowerCase() == "custody") {
            return (
                <>
                    <Button
                        disabled={!isSaveable}
                        type="button"
                        className="btn primary label-offset mb-20"
                        onClick={() => {
                            if (this.isValid()) {
                                this.onSaveCgtTransaction();
                            }
                        }}
                    >
                        {isEditingCGT ? "Save" : "Edit"}
                    </Button>
                    <table className="data-table no-sided-padding">
                        <tbody>
                            <tr>
                                <th>
                                    {InvestmentTransactionTypeMap[data.TransSource].toLocaleLowerCase() ==
                                    "fund redemption"
                                        ? Contents.CapitalGainPurpose
                                        : "Tax book cost:"}
                                </th>
                                <td>
                                    {isEditingCGT
                                        ? this.renderCGTValueTextBox()
                                        : cgtTransaction.CgtTotalValue != 0
                                        ? cgtTransaction.CgtTotalValue.toFixed(2)
                                        : null}
                                </td>
                            </tr>
                            <tr>
                                <th>Unit Tax Book Cost:</th>
                                <td>{isEditingCGT ? cgtUnitCost : unitCost}</td>
                            </tr>
                            <tr>
                                <th>Acquisition Date:</th>
                                <td>
                                    {isEditingCGT
                                        ? this.renderCGTDatePicker()
                                        : cgtTransaction.CgtDate
                                        ? JsonDateParser.getFormatDateString(
                                              JsonDateParser.toDate(cgtTransaction.CgtDate),
                                              "dd-MM-yyyy",
                                              true,
                                              true
                                          )
                                        : ""}
                                </td>
                            </tr>
                            <tr>
                                <th>CGT Exempt:</th>
                                <td>
                                    {isEditingCGT ? (
                                        <Checkbox
                                            value={cgtExempt}
                                            onChange={(e) => {
                                                this.setState({ cgtExempt: e });
                                            }}
                                        />
                                    ) : (
                                        <Checkbox disabled={true} value={cgtTransaction.CgtExempt} />
                                    )}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </>
            );
        }
    }

    private renderTransactionHistoryTable() {
        const { cgtTransactionHistory } = this.state;
        if (cgtTransactionHistory && cgtTransactionHistory.length > 0) {
            return (
                <table className="data-table no-sided-padding">
                    <thead>
                        <tr className="sub-head">
                            <th>Date</th>
                            <th>User</th>
                            <th className="cell-48">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {cgtTransactionHistory.map((item, index) => {
                            return (
                                <tr key={Guid.getNext() + index}>
                                    <td>{JsonDateParser.getFormatDate(item.DateChanged, "dd/MM/yyyy hh:mm:ss")}</td>
                                    <td>
                                        {item.FullName &&
                                        item.FullName.toUpperCase() == "SYSTEM USER" &&
                                        !(
                                            InvestmentTransactionTypeMap[
                                                this.state.data.TransSource
                                            ].toLocaleLowerCase() == "conversion in" ||
                                            InvestmentTransactionTypeMap[
                                                this.state.data.TransSource
                                            ].toLocaleLowerCase() == "conversion out"
                                        )
                                            ? "Created by wizard"
                                            : item.FullName}
                                    </td>
                                    <td>
                                        {item.LogInfo
                                            ? item.LogInfo.replace("_br /_", "\r\n").replace(" _br /_", "\r\n")
                                            : null}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            );
        } else {
            return null;
        }
    }

    private renderCGTValueTextBox() {
        const { cgtTotalValue } = this.state;
        return (
            <TextBoxEditor
                onChange={(e) => this.onTextBoxChange(e)}
                cssWrapper={"mb-0"}
                value={cgtTotalValue + ""}
                maxLength={10}
                validationRules={[
                    {
                        errorMessage: Contents.NumberValidation,
                        validator: (v) => {
                            if (!isNaN(Number(v))) {
                                this.setState({ isSaveable: true });
                                return true;
                            }
                            this.setState({ isSaveable: false });
                            return false;
                        },
                    },
                    {
                        errorMessage: Contents.DecimalPlaceValidation,
                        validator: (v) => {
                            const place = v.split(".");
                            if (!(place && place.length == 2 && place[1].length > 2)) {
                                this.setState({ isSaveable: true });
                                return true;
                            }
                            this.setState({ isSaveable: false });
                            return false;
                        },
                    },
                ]}
            />
        );
    }

    private renderCGTDatePicker() {
        const { cgtDateTime } = this.state;
        return (
            <DatePicker
                required={Contents.CanNotEmpty}
                cssWrapper={"mb-0"}
                labelSRSufix="dd/mm/yyyy"
                selected={cgtDateTime}
                showYearDropdown={true}
                alwaysValidateOnUpdate={false}
                onChange={(date) => this.setState({ cgtDateTime: date })}
                todayButton="Today"
                isClearable={false}
                dateFormat={"dd/MM/yyyy"}
                acceptDateFormats={["dd/MM/yyyy", "dd-MM-yyyy", "ddMMyyyy", "dd.MM.yyyy", "dd_MM_yyyy", "dd MM yyyy"]}
                acceptDateFormatsErrorMsg={Contents.AcceptDateFormatsErrorMsg}
            />
        );
    }

    private onTextBoxChange(e) {
        const { data } = this.state;
        const isNumber = Number(e);
        const place = e.split(".");
        if (!(place && place.length == 2 && place[1].length > 2)) {
            this.setState({
                isSaveable: true,
            });
        }
        if (!isNaN(isNumber)) {
            const unitCost = data.Quantity != 0 ? e / Math.abs(data.Quantity) : null;
            this.setState({
                cgtTotalValue: Number(e),
                cgtUnitCost: unitCost ? Number(unitCost.toFixed(4)) : null,
                isEditingCGT: true,
            });
        }
    }

    private onSaveCgtTransaction() {
        const { isEditingCGT, data, cgtTransaction } = this.state;
        const unitCost = data.Quantity != 0 ? cgtTransaction.CgtTotalValue / Math.abs(data.Quantity) : null;

        if (isEditingCGT) {
            const isEditCgtTotalValue = this.taxBookCostEdited();
            const isEditCgtExempt = this.cgtExemptEdited();
            const isEditCgtDate = this.acquisitionDateEdited();
            if (isEditCgtTotalValue || isEditCgtExempt || isEditCgtDate) {
                this.saveCgtTransaction();
            }
        } else {
            this.setState({
                isEditingCGT: !isEditingCGT,
                cgtExempt: cgtTransaction.CgtExempt,
                cgtDateTime: JsonDateParser.toDate(cgtTransaction.CgtDate),
                cgtUnitCost: unitCost ? Number(unitCost.toFixed(4)) : null,
                cgtTotalValue: cgtTransaction.CgtTotalValue,
                isSaveable: true,
            });
        }
    }

    private saveCgtTransaction() {
        const { cgtTotalValue, cgtExempt, cgtDateTime, cgtUnitCost, isEditingCGT } = this.state;
        const transaction: CgtTransaction = {
            ScripTransactionId: this.props.transId,
            CgtDate: JsonDateParser.toJsonDateNoOffset(cgtDateTime),
            CgtUnitCost: cgtUnitCost,
            CgtTotalValue: cgtTotalValue,
            CgtExempt: cgtExempt,
        };
        TransactionAction.saveCgtTransactions(transaction, (callBack: ServiceValidationError[]) => {
            if (callBack.length > 0) {
                this.setState({ errorMsg: callBack.map((ele) => ele.UserMessage) });
            } else {
                this.setState(
                    {
                        isEditingCGT: !isEditingCGT,
                        cgtExempt: cgtExempt,
                        cgtUnitCost: cgtUnitCost,
                        cgtTotalValue: cgtTotalValue,
                        isSaveable: true,
                    },
                    () => {
                        this.getData();
                    }
                );
            }
        });
    }

    private renderTemplate(title, value: number | string, isRenderCaw?: boolean): JSX.Element {
        if (typeof value == "string" || isRenderCaw) {
            return (
                <tr>
                    <th className="cell-26">{title}</th>
                    <td>{value}</td>
                </tr>
            );
        } else {
            return (
                <tr>
                    <th className="cell-26">{title}</th>
                    <td>{this.getMoneyFormat(value)}</td>
                </tr>
            );
        }
    }

    private getMoneyFormat(val: number): string {
        const formatMoney = MoneyDisplayHelper.FromatMoneyDisplay(val, CurrencyCode.GBP, false, 2);
        return formatMoney;
    }

    public render(): JSX.Element {
        return <>{this.props.showBox ? this.renderModal() : null}</>;
    }

    private taxBookCostEdited() {
        const { cgtTotalValue, cgtTransaction } = this.state;
        return (
            (!cgtTotalValue && cgtTransaction.CgtTotalValue) ||
            (cgtTotalValue && !cgtTransaction.CgtTotalValue) ||
            (cgtTotalValue && cgtTransaction.CgtTotalValue && cgtTotalValue != cgtTransaction.CgtTotalValue)
        );
    }
    private acquisitionDateEdited() {
        const { cgtDateTime, cgtExempt, cgtTotalValue, cgtUnitCost, cgtTransaction } = this.state;
        return (
            (!cgtDateTime && cgtTransaction.CgtDate) ||
            (cgtDateTime && !cgtTransaction.CgtDate) ||
            (cgtDateTime &&
                cgtTransaction.CgtDate &&
                JsonDateParser.getFormatDate(cgtDateTime, "dd-mm-yyyy") !=
                    JsonDateParser.getFormatDate(cgtTransaction.CgtDate, "dd-mm-yyyy"))
        );
    }

    private cgtExemptEdited() {
        const { cgtDateTime, cgtExempt, cgtTotalValue, cgtUnitCost, cgtTransaction } = this.state;
        return cgtExempt != cgtTransaction.CgtExempt;
    }
}
